import { AxiosResponse } from 'axios';
import { WEATHER_PAGE_SIZE } from 'ots-constants';
import { useMustacheTranslation, useOTSTranslation } from 'i18n/useOTSTranslation';
import { SidebarAd } from 'googlead/components/SidebarAd';
import {
  fetchRegionFeatured,
  fetchRegionMeta,
  fetchRegionOpeningDate, fetchRegionRelated, fetchRegionRelatedNews,
  fetchRegionResortsByStatus, fetchRegionResortListGuide,
} from 'api/region/static';
import {
  fetchResortDescriptionTabs,
  fetchResortSnowReport,
} from 'api/resort/static';
import { IRegionResortsPaginated, IRegionShort, IRegionFeaturedResort } from 'api/region/types';
import { CurrentLink, CurrentTab } from 'components/region/RegionHeaderNav/constants';
import { BoxWithShadow } from 'components/blocks/BoxWithShadow';
import { HeaderNavCurLink } from 'components/common/HeaderNav';
import { BlockContent } from 'components/blocks/BlockContent';
import { BlockHeader } from 'components/blocks/BlockHeader';
import { Main } from 'components/resort/Layout/Main';
import { Side } from 'components/resort/Layout/Side';
import { useRegionLayoutBubble } from 'page-components/region/_context';
import { IRegionLayout } from 'page-components/region/_layout';
import { RouteAndAnalyticsProps } from 'page-components/pathTypes';
import { getRegionSlug } from 'util/memcache';
import {
  IRelatedNews, ResortStatus, IResortPathInfo, IDescriptionsResort, IResortPageSnowInfo,
} from 'api/resort/types';
import getDailyFeaturedResortIndex from 'util/getDailyFeaturedResortIndex';
import { IFeaturedResort } from 'api/home/types';
import PlanYourTrip from 'components/common/PlanYourTrip/RegionWidget';
import { FeaturedResort } from 'components/home/FeaturedResort';

import { OpenDatesTable } from 'components/region/OpenDatesTable';
import { Hero400Error } from 'components/400';
import { formatDateFromStr, MINIMAL_DATE_FORMAT } from 'util/formatDate';
import SurfaceRelatedArticles from 'components/resort/SurfaceRelatedArticles';
import React from 'react';
import { assertShouldBeNot404, wrapServerSideProps, wrapStaticProps } from 'util/wrapProps';

export type RegionProjectedOpeningPageProps = {
  resorts: IRegionResortsPaginated,
  pathInfo: IRegionShort,
  openingDate: string,
  numResorts: number,
  relatedNews: IRelatedNews | null,
  featured: IRegionFeaturedResort,
  regionResortListGuide: Array<IResortPathInfo>,
  featuredResortByIndex: IResortPageSnowInfo | null,
  featuredResortDescriptionTabs?: Array<IDescriptionsResort> | null,
} & RouteAndAnalyticsProps & IRegionLayout;

export const DEBUG_TAG = 'region-projected-openings-page';
export const TEMPLATE_ID = '2.1.5';
export const TEMPLATE_NAME = 'Region Projected Opening';
const PAGE_RESORTS_STATUS = ResortStatus.openingDates;

export default function RegionProjectedOpeningsPage({
  resorts, numResorts, openingDate, pathInfo,
  relatedNews, regionResortListGuide, featuredResortDescriptionTabs,
  featuredResortByIndex,
}: RegionProjectedOpeningPageProps) {
  useRegionLayoutBubble({});
  const { t } = useOTSTranslation('common');
  const m = useMustacheTranslation('common');

  if (!resorts) {
    return <Hero400Error light />;
  }

  const featuredResort: IFeaturedResort | null = featuredResortByIndex ? {
    uuid: featuredResortByIndex.uuid,
    title: featuredResortByIndex.title,
    region: featuredResortByIndex.region,
    image: featuredResortByIndex.image,
    smallImage: featuredResortByIndex.smallImage ?? '',
    video: featuredResortByIndex.video ?? null,
    website: featuredResortByIndex.website ?? null,
    phone: featuredResortByIndex.phone ?? null,
    isPartner: featuredResortByIndex.isPartner,
    twitter: featuredResortByIndex.twitter ?? null,
    liftsUrl: featuredResortByIndex.liftsUrl ?? null,
    rentalUrl: featuredResortByIndex.rentalUrl ?? null,
    lessonsUrl: featuredResortByIndex.lessonsUrl ?? null,
    socialMediaAccounts: featuredResortByIndex.socialMediaAccounts ?? [],
  } : null;

  const seoTitle = pathInfo.isLogical ? t('region.openDates.logicSeoTitle', {
    region: pathInfo.title,
  }) : t('region.openDates.seoTitle', {
    region: pathInfo.title,
  });

  const seoText = pathInfo.isLogical ? m('region.openDates.logicSeoText', {
    region: pathInfo.title,
    numresorts: numResorts,
    firstopendate: formatDateFromStr(openingDate, MINIMAL_DATE_FORMAT),
  })
    : m('region.openDates.seoText', {
      region: pathInfo.title,
      numresorts: numResorts,
      firstopendate: formatDateFromStr(openingDate, MINIMAL_DATE_FORMAT),
    });

  return (
    <>
      <Main>
        <BoxWithShadow>
          <BlockHeader title={seoTitle} />
          <BlockContent>
            {seoText}
          </BlockContent>
        </BoxWithShadow>
        {!!resorts.pagination?.count && (
          <OpenDatesTable
            regionId={pathInfo.uuid}
            list={resorts}
            hasMore={resorts.pagination?.count > resorts.pagination?.limit}
          />
        )}
        <PlanYourTrip
          region={pathInfo}
          hasSkiRental={false}
          hasCarRental={false}
          hasFlights={false}
          isPartner={false}
        />
        {featuredResort && (
          <FeaturedResort
            resort={featuredResort}
            regionResorts={regionResortListGuide}
            resortDescriptionTabs={featuredResortDescriptionTabs ?? undefined}
            isOnRegionExceptSkiResorts
            utmCampaign="region_widget"
          />
        )}
      </Main>
      <Side>
        <SurfaceRelatedArticles
          relatedNewsList={relatedNews}
          isFresh
        />
        <SidebarAd isRegion id={pathInfo.uuid} showWeSki={false} />
      </Side>
    </>
  );
}

export const fetchProps = async ({ region }: { region: string }): Promise<RegionProjectedOpeningPageProps> => {
  const cache: AxiosResponse<IRegionShort> = await assertShouldBeNot404(() => getRegionSlug(region));
  const { uuid } = cache.data;
  const featured = await fetchRegionFeatured(uuid);
  const resorts = await fetchRegionResortsByStatus(uuid, PAGE_RESORTS_STATUS, 0, WEATHER_PAGE_SIZE);

  const partnerResortsResponse = await fetchRegionResortsByStatus(
    uuid,
    ResortStatus.partner,
    1,
    100,
  );
  const partnerResorts = partnerResortsResponse?.data ?? [];

  let featuredResortByIndex: IResortPageSnowInfo | null = null;
  if (partnerResorts.length === 1) {
    featuredResortByIndex = await fetchResortSnowReport(partnerResorts[0].uuid);
  } else if (partnerResorts.length > 1) {
    const dailyIndex = getDailyFeaturedResortIndex(partnerResorts.length);
    featuredResortByIndex = await fetchResortSnowReport(partnerResorts[dailyIndex].uuid);
  }

  return {
    type: 'region',
    route: 'projected-openings',
    resorts,
    featured,
    featuredResortByIndex,
    pathInfo: cache.data,
    numResorts: (await fetchRegionMeta(uuid))?.resorts?.active,
    openingDate: await fetchRegionOpeningDate(uuid),
    relatedNews: await fetchRegionRelatedNews(uuid),
    regionResortListGuide: await fetchRegionResortListGuide(uuid),
    featuredResortDescriptionTabs: featuredResortByIndex?.uuid
      ? await fetchResortDescriptionTabs(featuredResortByIndex.uuid.toString())
      : null,

    /**
       * Layout
       */
    related: await fetchRegionRelated(uuid),
    navigation: {
      head: HeaderNavCurLink.SNOW_REPORTS,
      tab: CurrentTab.TRIP_PLANNING,
      link: CurrentLink.PROJECTED_OPENINGS,
    },
    adTKey: 'metaregion projected-openings',
    templateName: TEMPLATE_NAME,
    templateId: TEMPLATE_ID,
    hero: {
      title: 'region.openDates.heroTitle',
    },
    meta: {
      redirectTo: cache.data.domain ?? null,
      description: 'MetaRegion.projected_openings.description',
      title: 'MetaRegion.projected_openings.title',
    },
    prefetch: false,
  };
};

// This function gets called at build time
// eslint-disable-next-line max-len
export const getRegionProjectedOpeningsPageStaticProps = wrapStaticProps(fetchProps);

export const getRegionProjectedOpeningsPageServerSideProps = wrapServerSideProps(fetchProps);
